import React, { useState, useEffect } from 'react';
import './App.css';
import WelcomeScreen from './components/WelcomeScreen';
import PersonalInfoForm from './components/PersonalInfoForm';
import QuestionCard from './components/QuestionCard';
import ConsequenceQuestion from './components/ConsequenceQuestion';
import LoadingScreen from './components/LoadingScreen';
import ResultScreen from './components/ResultScreen';
import BookingScreen from './components/BookingScreen';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import { 
  QUESTIONS, 
  calculateFrictionScore,
  calculateDimensionScores
} from './data/mock';

function App() {
  // Screens: welcome, personalInfo, quiz, consequence, loading, results, booking, admin, adminDashboard
  const [currentScreen, setCurrentScreen] = useState('welcome');
  const [language, setLanguage] = useState('fr');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [userInfo, setUserInfo] = useState(null);
  const [results, setResults] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [selectedConsequence, setSelectedConsequence] = useState(null);

  const questions = QUESTIONS[language];

  // Vérifier le token admin au démarrage
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setAdminToken(token);
    }
    
    // Vérifier l'URL pour la route admin
    if (window.location.pathname === '/admin') {
      setCurrentScreen('admin');
    }
  }, []);

  const handleStart = () => {
    setCurrentScreen('personalInfo');
  };

  const handlePersonalInfoSubmit = (info) => {
    setUserInfo(info);
    setCurrentScreen('quiz');
    setCurrentQuestion(0);
    setAnswers({});
  };

  const handleAnswerSelect = (value) => {
    setAnswers({
      ...answers,
      [currentQuestion]: value
    });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Toutes les questions répondues, passer à la question d'auto-positionnement
      setCurrentScreen('consequence');
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleConsequenceSelect = (option) => {
    setSelectedConsequence(option);
  };

  const handleConsequenceContinue = () => {
    // Passer à l'écran de chargement
    setCurrentScreen('loading');
  };

  const handleLoadingComplete = async () => {
    // Calculer les résultats avec gestion des questions inversées
    const { frictionIndex, fluidityScore, totalScore } = calculateFrictionScore(answers, questions);
    const dimensionScores = calculateDimensionScores(answers, questions);

    const resultsData = {
      totalQuestions: questions.length,
      completedAt: new Date().toISOString(),
      answers,
      frictionIndex,
      fluidityScore,
      totalScore,
      dimensionScores,
      selfPositioning: selectedConsequence?.text || null,
      selfPositioningLevel: selectedConsequence?.level || null
    };

    setResults(resultsData);

    // Envoyer au backend
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessment/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userInfo,
          results: resultsData,
          language,
          diagnosticType: 'friction'
        }),
      });

      if (!response.ok) {
        console.error('Erreur lors de l\'envoi des résultats');
      }
    } catch (error) {
      console.error('Erreur:', error);
    }

    setCurrentScreen('results');
  };

  const handleBooking = () => {
    setCurrentScreen('booking');
  };

  const handleAdminLogin = (token) => {
    setAdminToken(token);
    localStorage.setItem('admin_token', token);
    setCurrentScreen('adminDashboard');
  };

  const handleAdminLogout = () => {
    setAdminToken(null);
    localStorage.removeItem('admin_token');
    setCurrentScreen('admin');
  };

  // Render based on current screen
  const renderScreen = () => {
    switch (currentScreen) {
      case 'welcome':
        return (
          <WelcomeScreen 
            onStart={handleStart} 
            onAdmin={() => setCurrentScreen('admin')}
            language={language}
          />
        );

      case 'personalInfo':
        return (
          <PersonalInfoForm 
            onSubmit={handlePersonalInfoSubmit}
            language={language}
          />
        );

      case 'quiz':
        return (
          <QuestionCard
            question={questions[currentQuestion]}
            questionNumber={currentQuestion + 1}
            totalQuestions={questions.length}
            selectedAnswer={answers[currentQuestion]}
            onAnswerSelect={handleAnswerSelect}
            onNext={handleNext}
            onPrevious={handlePrevious}
            language={language}
            canGoPrevious={currentQuestion > 0}
            isLastQuestion={currentQuestion === questions.length - 1}
          />
        );

      case 'consequence':
        return (
          <ConsequenceQuestion
            selectedOption={selectedConsequence}
            onSelect={handleConsequenceSelect}
            onContinue={handleConsequenceContinue}
            language={language}
          />
        );

      case 'loading':
        return (
          <LoadingScreen
            onComplete={handleLoadingComplete}
          />
        );

      case 'results':
        return (
          <ResultScreen
            results={results}
            onBooking={handleBooking}
          />
        );

      case 'booking':
        return (
          <BookingScreen
            userInfo={userInfo}
            results={results}
            language={language}
          />
        );

      case 'admin':
        return (
          <AdminLogin 
            onLogin={handleAdminLogin}
            language={language}
          />
        );

      case 'adminDashboard':
        return (
          <AdminDashboard
            token={adminToken}
            onLogout={handleAdminLogout}
            language={language}
          />
        );

      default:
        return (
          <WelcomeScreen 
            onStart={handleStart} 
            onAdmin={() => setCurrentScreen('admin')}
            language={language}
          />
        );
    }
  };

  return (
    <div className="App">
      {renderScreen()}
    </div>
  );
}

export default App;
